<template>
  <div class="fts-calendar" @scroll="onScroll">
    <div class="fts-client-header" :class="{ scrolled: scroll }">
      <div class="client-header-center">
        <font-awesome-icon
          :icon="['far', 'calendar']"
          fixed-width
          size="lg"
          class="client-header-icon"
        />
        <div class="client-header-title">My Calendar</div>
      </div>
    </div>

    <div class="selection-container">
      <div class="selection-outer">
        <div
          class="selection-wrapper"
          v-for="selection in selections.filter((x) => !x.deleted)"
          :key="selection.id"
          :style="{ borderColor: selection.color }"
          :class="{ disabled: !selection.show }"
          @click="toggleSelection(selection)"
        >
          <font-awesome-icon
            v-if="
              !selection.task &&
              (!selection.everyone ||
                (roles &&
                  roles.some(
                    (x) =>
                      x == 'Workspace Admin' ||
                      x == 'Consultant' ||
                      x == 'Administrator'
                  )))
            "
            :icon="['fas', 'trash']"
            class="selection-remove stopPropagation"
            @click="removeSelection(selection)"
          />

          <div class="selection-info" v-if="selection.task">
            <div class="selection-name">My Task Dues</div>
          </div>

          <div class="selection-info list" v-if="selection.listId">
            <fts-moji
              :emoji="selection.listEmoji"
              :size="16"
              class="selection-emoji"
              v-if="selection.listEmoji"
            />
            <div class="selection-name">{{ selection.listName }}</div>
          </div>
          <div class="selection-info">
            <fts-moji
              :emoji="selection.moduleEmoji"
              :size="16"
              class="selection-emoji"
              v-if="selection.moduleEmoji"
            />
            <div class="selection-name">{{ selection.moduleName }}</div>
          </div>
          <div class="selection-info" v-if="selection.everyone">
            <font-awesome-icon
              style="color: #9e9e9e; margin-left: 3px"
              :icon="['fas', 'user-friends']"
              fixed-width
            />
          </div>
          <swatches
            v-if="!selection.task"
            row-length="5"
            :swatches="colorSchemes"
            shapes="circles"
            popover-x="right"
            popover-y="top"
            :swatch-size="25"
            :trigger-style="{ width: '18px', height: '18px' }"
            v-model="selection.color"
            class="selection-picker stopPropagation"
            @input="getCalendarEvents"
          />
        </div>
      </div>
    </div>
    <!-- full calendar -->
    <fts-full-calendar
      :events="events"
      @prev="getCalendarEvents"
      @next="getCalendarEvents"
      @eventClick="eventClick"
      class="calendar-body"
      ref="ftsFullCalendar"
    />
  </div>
</template>

<script>
import moment from "moment";
import Swatches from "vue-swatches";

import commons from "../commons/commons";
import { clientStore } from "../store/clientStore";
import { authorizationStore } from "../store/authorizationStore";

import FTSMoji from "../elements/FTSMoji";
import FTSToggleSwitch from "../elements/FTSToggleSwitch";
import FTSFullCalendar from "../elements/FTSFullCalendar";

export default {
  components: {
    "fts-moji": FTSMoji,
    "fts-toggle-switch": FTSToggleSwitch,
    "fts-full-calendar": FTSFullCalendar,
    Swatches,
  },
  data: () => ({
    events: [],
    selections: [],
    colorSchemes: commons.darkPalette,
    scroll: false,
  }),
  methods: {
    moment,
    onScroll(e) {
      this.scroll = e.target.scrollTop > 0;
    },
    eventClick(data) {
      window.eventBus.$emit("quickViewData", {
        items: [
          { ...data.payload, documentId: data.payload.document && data.payload.document.documentId },
        ],
      });
    },
    toggleSelection(selection) {
      if (commons.elementStopPropagation()) return;
      this.$set(selection, "show", !selection.show);
      this.getCalendarEvents();
    },
    removeSelection(selection) {
      this.$set(selection, "deleted", true);
      this.getCalendarEvents();
    },
    async getCalendarEvents() {
      let payload = {
        startDate: moment(
          this.$refs.ftsFullCalendar.fullCalendarApi.view.activeStart
        ).format("YYYY-MM-DD"),
        endDate: moment(
          this.$refs.ftsFullCalendar.fullCalendarApi.view.activeEnd
        ).format("YYYY-MM-DD"),
        workspaceOnly: null,
      };

      if (this.selections)
        payload = {
          ...payload,
          selections: this.selections,
        };

      try {
        let res = await clientStore.dispatch("getMyCalender", payload);
        this.selections = res.data.selections.map((x) => {
          let selection = x;
          const findModule = this.workspaceModules.find(
            (y) => y.id == x.vectorId
          );
          if (findModule) {
            selection.moduleName = findModule.name;
            selection.moduleEmoji = findModule.emoji;
            if (x.listId) {
              const findList = findModule.menus.find((y) => y.id === x.listId);
              if (findList) {
                selection.listName = findList.name;
                selection.listEmoji = findList.emoji;
              }
            }
          }
          return selection;
        });

        this.events = res.data.calendarEvents.map((x) => {
          let event = {
            id: commons.nanoID(),
            entryId: x.id,
            title: `${x.title}`,
            start: x.start,
            payload: x,
          };
          const findCalendar = this.selections.find(
            (y) => y.id == x.calendarId
          );
          if (findCalendar && findCalendar.color)
            event.color = findCalendar.color;
          if (x.end) event.end = x.end;

          return event;
        });
      } catch (err) {
        console.error(err);
      }
    },
  },
  computed: {
    roles() {
      return authorizationStore.state.roles;
    },
    workspaceModules() {
      return clientStore.state.workspaceModules;
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.getCalendarEvents();
    });
  },
};
</script>

<style lang="scss" scoped>
@import "./src/assets/scss/vars.scss";

.fts-calendar {
  height: 100%;
  overflow: auto;
  .calendar-body {
    padding: 1rem;
  }
  .selection-container {
    padding: 0 1rem;
    .selection-outer {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      margin: -0.375rem;
      .selection-wrapper {
        position: relative;
        min-width: 0;
        display: flex;
        align-items: center;
        margin: 0.375rem;
        padding: 0.75rem;
        border: 1px solid var(--fts-gray-3);
        border-radius: 0.25rem;
        color: var(--fts-text);
        cursor: pointer;
        &.disabled {
          .selection-info {
            filter: grayscale(75%);
          }
        }
        .selection-info {
          min-width: 0;
          display: flex;
          align-items: center;
          &.list {
            margin-right: 0.75rem;
          }
          .selection-emoji {
            margin-right: 0.375rem;
          }
          .selection-name {
            @include text-overflow-hidden;
            font-size: 0.875rem;
          }
        }
        .selection-picker {
          margin-left: 0.75rem;
          display: inline-flex;
        }
        .selection-remove {
          margin-right: 0.75rem;
          font-size: 0.875rem;
          color: var(--fts-gray-5);
          cursor: pointer;
          &:hover {
            color: var(--fts-danger);
          }
        }
      }
    }
  }
}
</style>